/**
 * ADR-0038 3b part 2 — env-claim enforcement (planner web).
 *
 * The TLD is the environment (.com = prod, .dev = develop, .xyz = demo) and
 * each bundle is bound to ONE Firestore database (`__FIRESTORE_DATABASE_ID__`).
 * Firebase Auth is PROJECT-wide, so a demo persona can present a valid token
 * to the production bundle. This module decides whether the token may use
 * this build; auth.ts signs a refused session out (fresh sign-in AND
 * restored session alike) and shows the message on the login screen.
 *
 *   token env absent → real staff/client account: allowed on PROD and on
 *                      DEVELOP (develop is a pseudonymised clone of prod with
 *                      uids preserved — staff use their own accounts there;
 *                      the one deliberate relaxation of ADR-0038 §4.1).
 *   token env == build env → allowed.
 *   anything else    → refused, fail closed, with a message naming where the
 *                      account belongs.
 *
 * Twin of app.carer/lib/services/env_gate.dart and
 * app.circleofsupport/lib/infrastructure/core/env_gate.dart — same rules.
 * Pure: no Firebase imports, no build-time constants — so it unit-tests.
 */

export type AppEnv = "prod" | "develop" | "demo" | "test" | "training" | "nonprod";

/** The non-production GCP/Firebase project (its own sign-in pool, one database). */
export const NONPROD_FIREBASE_PROJECT = "stc-nonproduction";

/**
 * The environment a BUILD belongs to (Ed 2026-10-10: a fifth env, `nonprod`).
 * The Firebase project decides first: stc-nonproduction's "(default)" database
 * has the same name as production's, so the database name alone would call a
 * non-prod build "prod". Every other project falls through to the
 * database-name rule, unchanged.
 *
 * 2026-10-21 (Ed): dev/test/demo/training all move into stc-nonproduction, as
 * NAMED databases, and its `nonprod` environment becomes demo. So in that
 * project the database name decides too, and only "(default)" (being emptied)
 * is still `nonprod`.
 */
export function appEnvForBuild(firebaseProject: string, databaseId: string): AppEnv {
  if (firebaseProject === NONPROD_FIREBASE_PROJECT && databaseId === "(default)") return "nonprod";
  return appEnvForDatabase(databaseId);
}

export function appEnvForDatabase(databaseId: string): AppEnv {
  switch (databaseId) {
    case "develop": return "develop";
    case "demo": return "demo";
    case "test": return "test";
    case "training": return "training";
    default: return "prod"; // "(default)" — and any unknown id refuses every env-stamped token
  }
}

const SURFACE_URL: Record<AppEnv, string> = {
  prod: "app.sweettreeconnect.com",
  develop: "app.sweettreeconnect.dev",
  demo: "app.sweettreeconnect.xyz",
  test: "app.sweettreeconnect.tech",
  training: "app.sweettreeconnect.training",
  nonprod: "stc-nonproduction.web.app",
};

function parseEnv(raw: string): AppEnv | null {
  switch (raw) {
    case "develop": return "develop";
    case "demo": return "demo";
    case "test": return "test";
    case "training": return "training";
    case "nonprod": return "nonprod";
    default: return null;
  }
}

/** `null` = the token may use this build; otherwise the refusal message. */
export function envRefusal(build: AppEnv, tokenEnv: unknown): string | null {
  // A malformed (non-string) env is not "absent" — it is a claim we cannot
  // interpret, and it fails closed exactly as the callables'
  // database_for_claims does.
  if (tokenEnv !== undefined && tokenEnv !== null && typeof tokenEnv !== "string") {
    return `This account carries an environment claim this app cannot read. You have been signed out of ${build}.`;
  }
  const raw = typeof tokenEnv === "string" ? tokenEnv.trim() : "";
  if (raw === "") {
    // nonprod: its OWN Firebase project and sign-in pool, so an account with no
    // env claim there can only be a non-prod account (a person invited into
    // non-prod); no production account can exist in that pool.
    if (build === "prod" || build === "develop" || build === "nonprod") return null;
    return `This is the ${build} environment. Real staff accounts sign in at ${SURFACE_URL.prod}.`;
  }
  const tokenAppEnv = parseEnv(raw);
  if (tokenAppEnv === build) return null;
  const where = tokenAppEnv === null
    ? `an environment this app does not recognise ("${raw}")`
    : `the ${tokenAppEnv} environment — sign in at ${SURFACE_URL[tokenAppEnv]}`;
  return `This account belongs to ${where}. You have been signed out of ${build}.`;
}

/**
 * Whether a session belongs to a different AUTH POOL than this build signs
 * into. `null`/empty tenant = the main pool, on either side.
 *
 * ☠️ This is a DIFFERENT AXIS from `envRefusal`, and the gap between them is
 * how a stale session survived a cutover (2026-09-21). `envRefusal` reads the
 * token's `env` claim. When app.sweettreeconnect.xyz moved from the main pool
 * into the `demo-5agib` tenant, the main-pool and tenant copies of
 * demo.planner@ BOTH carried `env: demo` — so a session saved before the
 * move passed the environment gate and was admitted on the orphaned
 * main-pool credential. Setting `auth.tenantId` scopes NEW sign-ins only; a
 * restored session comes back from whichever pool it came from.
 *
 * Twin of app.carer / app.circleofsupport `sessionFromAnotherPool`. Keep the
 * three in step.
 */
export function sessionFromAnotherPool(
  sessionTenantId: string | null | undefined,
  buildTenantId: string | null | undefined,
): boolean {
  return (sessionTenantId ?? "") !== (buildTenantId ?? "");
}

/** Shown on the login card after a cross-pool session is signed out. */
export const ANOTHER_POOL_MESSAGE =
  "This site's sign-in has moved. Please sign in again.";
